/**
 * Responsibility: one observed event, with the buttons that matter.
 *
 * The cart link is prominent because that is the whole point of the alert.
 * Both links are real retailer URLs, never a redirect through the hub - a
 * redirect breaks universal-link matching and the retailer app will not open.
 */
import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { StatusChip } from '../ui/StatusChip';
import type { StockEvent } from '../../api/client';

type EventCardProps = {
  event: StockEvent;
};

function priceLine(event: StockEvent): string {
  const price = event.offer.price;
  if (price === null || price === undefined) return 'price unknown';
  const msrp = event.msrp;
  if (msrp === null || msrp === undefined || event.msrp_confidence === 'UNKNOWN') {
    return `$${price} (MSRP unknown)`;
  }
  const delta = Number(price) - Number(msrp);
  if (delta === 0) return `$${price} (at MSRP)`;
  const direction = delta > 0 ? 'over' : 'under';
  return `$${price} ($${Math.abs(delta).toFixed(2)} ${direction} MSRP)`;
}

export function EventCard({ event }: EventCardProps) {
  const inStock = event.offer.availability === 'IN_STOCK';

  return (
    <Card variant="outlined" sx={inStock ? { borderColor: 'primary.main' } : undefined}>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {event.retailer}
            </Typography>
            <StatusChip kind="availability" value={event.offer.availability} />
            <StatusChip kind="seller" value={event.offer.seller_type} />
            {event.offer.status !== 'OK' ? (
              <StatusChip kind="status" value={event.offer.status} />
            ) : null}
            {event.degraded_path ? (
              <StatusChip kind="status" value="ERROR" />
            ) : null}
          </Stack>

          <Typography variant="body1">{priceLine(event)}</Typography>

          <Typography variant="body2" color="text.secondary">
            {event.node_id} at {new Date(event.observed_at).toLocaleTimeString()}
            {event.degraded_path ? ' - sent directly by the node, hub was unreachable' : ''}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {event.cart_url ? (
              <Button
                variant="contained"
                size="small"
                href={event.cart_url}
                target="_blank"
                rel="noopener"
                endIcon={<OpenInNewIcon />}
              >
                Add to cart
              </Button>
            ) : null}
            <Button
              size="small"
              variant="outlined"
              href={event.product_url}
              target="_blank"
              rel="noopener"
              endIcon={<OpenInNewIcon />}
            >
              View product
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
